import { notify } from "@portal/BaseComponents/Notifications";
import { portalTabQueryKeys } from "@portal/Components/Portal/PotalTabs/types";
import {
  deleteADispute,
  getDisputeDetails,
  getDisputeList,
  getDisputeTypes,
  raiseADispute,
  updateADispute,
  updateDisputeStatus,
} from "@portal/api/dispute-list";
import { AppPageParams } from "@portal/lib/router/constants/page-info";
import { DisputeListFilters } from "@portal/store/Filters/dipsutes/type";
import { tableAtom } from "@portal/store/table/table";
import { RequestConfig } from "@portal/types/common";
import {
  CreateDispute,
  DisputeDetails,
  DisputeParams,
} from "@portal/types/entities/dispute";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import produce from "immer";
import { useSetAtom } from "jotai";
import { useParams } from "react-router-dom";

interface DisputeQueryParams {
  successCallBack?: () => void;
}
function useDisputeListQuery(params: RequestConfig<DisputeListFilters>) {
  const setTableValue = useSetAtom(tableAtom);

  return useQuery({
    queryKey: [portalTabQueryKeys.DISPUTES, params],
    queryFn: () => getDisputeList(params),
    retry: false,
    async onSuccess(data) {
      setTableValue((value) => {
        return produce(value, (draft) => {
          draft.bulk_records = data.total_records;
          return draft;
        });
      });
    },
    onError(err) {},
  });
}

function useDisputeTypesQuery(params?: Partial<DisputeParams>) {
  return useQuery({
    queryKey: ["dispute-types"],
    queryFn: () => getDisputeTypes(params),
    retry: false,
  });
}
function useDisputeDetailsQuery(params?: string) {
  return useQuery({
    queryKey: ["dispute-details", params],
    queryFn: () => getDisputeDetails(params),
    retry: false,
    enabled: Boolean(params?.length),
  });
}
function useUpdateDisputeDetailsQuery(props: DisputeQueryParams) {
  const { successCallBack } = props;
  const queryClient = useQueryClient();
  const { dispute_id } = useParams<AppPageParams>();
  return useMutation({
    mutationKey: ["update-dispute-details"],
    mutationFn: (data: Partial<CreateDispute>) =>
      updateADispute(data, dispute_id),
    onSuccess(data) {
      const currentData = queryClient.getQueriesData<DisputeDetails>([
        "dispute-details",
        dispute_id,
      ]);
      queryClient.invalidateQueries([portalTabQueryKeys.DISPUTES]);
      if (!currentData.length) return;
      const [queryKey] = currentData[0];

      queryClient.setQueryData(queryKey, data);
      notify.success("Dispute updated successfully");
      successCallBack?.();
    },
    onError() {
      notify.error("Failed to update dispute. Please try again later.");
    },
  });
}

function useUpdateDisputeStatus() {
  const { dispute_id } = useParams<AppPageParams>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-dispute-status"],
    mutationFn: (data: Partial<CreateDispute>) =>
      updateDisputeStatus(data, dispute_id),
    onSuccess(data) {
      const currentData = queryClient.getQueriesData<DisputeDetails>([
        "dispute-details",
        dispute_id,
      ]);

      queryClient.invalidateQueries([portalTabQueryKeys.DISPUTES]);

      if (!currentData.length) return;
      const [queryKey] = currentData[0];

      queryClient.setQueryData(queryKey, data);
      notify.success("Dispute status updated successfully");
    },
    onError() {
      notify.error("Failed to update dispute status. Please try again later.");
    },
  });
}
function useDeleteDisputeQuery(props: DisputeQueryParams) {
  const { successCallBack } = props;
  const queryClient = useQueryClient();
  const { dispute_id } = useParams<AppPageParams>();
  return useMutation({
    mutationKey: ["delete-dispute"],
    mutationFn: () => deleteADispute(dispute_id),
    onSuccess() {
      notify.success("Dispute deleted successfully");
      queryClient.invalidateQueries([portalTabQueryKeys.DISPUTES]);
      successCallBack?.();
    },
    onError() {
      notify.error("Failed to delete dispute. Please try again later.");
    },
  });
}

function useRaiseADisputeQuery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["raise-a-disputes"],
    mutationFn: (data: CreateDispute) => raiseADispute(data),
    onSuccess() {
      queryClient.invalidateQueries([portalTabQueryKeys.DISPUTES]);
      notify.success("Dispute Created Successfully");
    },
    onError() {
      notify.error("Failed to create dispute. Please try again later.");
    },
  });
}

export {
  useDeleteDisputeQuery,
  useDisputeDetailsQuery,
  useDisputeListQuery,
  useDisputeTypesQuery,
  useRaiseADisputeQuery,
  useUpdateDisputeDetailsQuery,
  useUpdateDisputeStatus,
};
